import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import {
  FiX, FiImage, FiDroplet, FiUploadCloud, FiMaximize,
  FiTrash2, FiPlus, FiAlertCircle, FiMoon, FiFolder,
  FiLink, FiZap, FiLoader, FiLayers
} from 'react-icons/fi';
import supabase from '../../supabase/supabase';

const ContainerPopup = ({ onClose, onUpdate, currentStyles = {}, onOpenLibrary, onDelete }) => {
  const [styles, setStyles] = useState({
    backgroundType: currentStyles.backgroundType || 'color',
    backgroundColor: currentStyles.backgroundColor || '#ffffff',
    backgroundImage: currentStyles.backgroundImage || '',
    backgroundSize: currentStyles.backgroundSize || 'cover',
    paddingTop: currentStyles.paddingTop !== undefined ? currentStyles.paddingTop : (currentStyles.padding || 48),
    paddingBottom: currentStyles.paddingBottom !== undefined ? currentStyles.paddingBottom : (currentStyles.padding || 48),
    overlayOpacity: currentStyles.overlayOpacity !== undefined ? currentStyles.overlayOpacity : 0,
    overlayColor: currentStyles.overlayColor || '#000000',
    anchorId: currentStyles.anchorId || '',
    fullScreen: currentStyles.fullScreen || false,
    parallax: currentStyles.parallax || false,
    parallaxSpeed: currentStyles.parallaxSpeed !== undefined ? currentStyles.parallaxSpeed : 0.4,
  });

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadErrorMsg, setUploadErrorMsg] = useState('');

  // AI Image Generation state
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiGeneratedUrl, setAiGeneratedUrl] = useState('');

  const colors = [
    '#ffffff', '#f9fafb', '#f3f4f6', '#e5e7eb', '#111827',
    '#2dd4bf', '#3b82f6', '#6366f1', '#f43f5e'
  ];

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadErrorMsg('');
    if (!file.type.startsWith('image/')) {
      setUploadErrorMsg('Invalid file type.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setUploadErrorMsg('File is too large. Maximum 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const filePath = `backgrounds/${fileName}`;

      setUploadProgress(30);

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file, { cacheControl: '3600', upsert: false });

      if (uploadError) throw new Error(uploadError.message || 'Upload failed.');

      setUploadProgress(80);

      const { data: publicUrlData } = supabase.storage.from('media').getPublicUrl(filePath);
      if (!publicUrlData?.publicUrl) throw new Error('Failed to retrieve the public URL.');

      setStyles(prev => ({ ...prev, backgroundImage: publicUrlData.publicUrl, backgroundType: 'image' }));
      setUploadProgress(100);
    } catch (error) {
      setUploadErrorMsg(error.message || 'An unexpected error occurred.');
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        e.target.value = '';
      }, 500);
    }
  };

  const saveAIImageToLibrary = async (imageUrl) => {
    try {
      // Fetch the AI-generated image
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      // Generate unique filename
      const fileName = `ai-generated-${Date.now()}-${Math.random().toString(36).substring(2, 8)}.png`;
      const filePath = `backgrounds/${fileName}`;
      
      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, blob, { 
          cacheControl: '3600', 
          upsert: false,
          contentType: 'image/png'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: publicUrlData } = supabase.storage.from('media').getPublicUrl(filePath);
      
      return publicUrlData?.publicUrl || imageUrl;
    } catch (err) {
      console.error('Failed to save AI image to library:', err);
      // Return original URL if save fails
      return imageUrl;
    }
  };

  const handleGenerateAIImage = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    setAiError('');
    setAiGeneratedUrl('');

    try {
      const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
      if (!apiKey) throw new Error('OpenAI API key not configured.');

      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'dall-e-3',
          prompt: aiPrompt,
          n: 1,
          size: '1792x1024',
          quality: 'standard',
        }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error?.message || `API error ${response.status}`);
      }

      const data = await response.json();
      const url = data?.data?.[0]?.url;
      if (!url) throw new Error('No image returned from AI.');

      // Save to library in background
      const savedUrl = await saveAIImageToLibrary(url);
      setAiGeneratedUrl(savedUrl);
    } catch (err) {
      setAiError(err.message || 'Failed to generate image.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleUseAIImage = () => {
    if (!aiGeneratedUrl) return;
    setStyles(prev => ({ ...prev, backgroundImage: aiGeneratedUrl, backgroundType: 'image' }));
    setAiGeneratedUrl('');
    setAiPrompt('');
    setAiError('');
  };

  const overlayPercent = Math.round(styles.overlayOpacity * 100);

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden border border-white/20"
      >
        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/80">
          <div>
            <h3 className="font-black text-gray-900 text-lg uppercase tracking-tight">Section Styling</h3>
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-0.5">Customize your container</p>
          </div>
          <div className="flex items-center space-x-2">
            {onDelete && (
              <button
                onClick={() => { onDelete(); onClose(); }}
                className="p-2.5 bg-red-50 text-red-500 rounded-xl hover:bg-red-100 transition-all border border-red-200"
                title="Delete Section"
              >
                <SafeIcon icon={FiTrash2} className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="bg-white p-2 rounded-xl shadow-sm hover:shadow-md transition-all text-gray-400 hover:text-gray-600 border border-gray-100"
            >
              <SafeIcon icon={FiX} className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-8 space-y-8 max-h-[70vh] overflow-y-auto custom-scrollbar">

          <div className="flex p-1.5 bg-gray-100 rounded-2xl">
            {['color', 'image'].map((type) => (
              <button
                key={type}
                onClick={() => {
                  setStyles({ ...styles, backgroundType: type });
                  setUploadErrorMsg('');
                  setAiError('');
                  setAiGeneratedUrl('');
                }}
                className={`flex-1 flex items-center justify-center space-x-2 py-3 rounded-xl text-sm font-black uppercase tracking-wider transition-all ${styles.backgroundType === type ? 'bg-white shadow-xl text-teal-600 scale-100' : 'text-gray-400 hover:text-gray-600 scale-95'}`}
              >
                <SafeIcon icon={type === 'color' ? FiDroplet : FiImage} className="w-4 h-4" />
                <span>{type}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            {styles.backgroundType === 'color' ? (
              <motion.div key="color" initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 10 }} className="space-y-4">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-4">Pick a Color Palette</label>
                <div className="grid grid-cols-5 gap-3">
                  {colors.map(c => (
                    <button
                      key={c}
                      onClick={() => setStyles({ ...styles, backgroundColor: c })}
                      className={`aspect-square rounded-2xl border-4 transition-all hover:scale-110 ${styles.backgroundColor === c ? 'border-teal-400 shadow-lg shadow-teal-100' : 'border-white shadow-sm'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                  <div className="relative aspect-square rounded-2xl border-4 border-white shadow-sm overflow-hidden group">
                    <input
                      type="color"
                      value={styles.backgroundColor}
                      onChange={(e) => setStyles({ ...styles, backgroundColor: e.target.value })}
                      className="absolute inset-0 w-[200%] h-[200%] -top-1/2 -left-1/2 cursor-pointer"
                    />
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-400 group-hover:text-teal-500 transition-colors">
                      <SafeIcon icon={FiPlus} />
                    </div>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div key="image" initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -10 }} className="space-y-6">
                <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block">Background Visuals</label>

                {uploadErrorMsg && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-red-50 border border-red-200 text-red-600 rounded-xl p-3 flex items-start space-x-3">
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5 shrink-0 mt-0.5" />
                    <div className="text-xs font-medium leading-relaxed"><b>Upload Failed:</b><br />{uploadErrorMsg}</div>
                  </motion.div>
                )}

                {styles.backgroundImage ? (
                  <div className="relative group aspect-video rounded-3xl overflow-hidden border-4 border-white shadow-2xl bg-gray-100">
                    <div 
                      className="absolute inset-0 transition-transform duration-700 group-hover:scale-105"
                      style={{
                        backgroundImage: `url(${styles.backgroundImage})`,
                        backgroundSize: styles.backgroundSize || 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      }}
                    />
                    {styles.overlayOpacity > 0 && (
                      <div className="absolute inset-0 pointer-events-none" style={{ backgroundColor: styles.overlayColor, opacity: styles.overlayOpacity }} />
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-wrap items-center justify-center gap-3 backdrop-blur-sm p-4">
                      <label className="flex items-center space-x-1.5 px-3 py-2 bg-white text-teal-600 rounded-xl cursor-pointer hover:scale-105 transition-transform shadow-xl font-bold text-xs">
                        <SafeIcon icon={FiUploadCloud} className="w-4 h-4" />
                        <span>Change Image</span>
                        <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={handleFileUpload} />
                      </label>
                      {onOpenLibrary && (
                        <button onClick={() => onOpenLibrary((image) => { setStyles(prev => ({ ...prev, backgroundImage: image.url, backgroundType: 'image' })); })} className="flex items-center space-x-1.5 px-3 py-2 bg-blue-500 text-white rounded-xl hover:scale-105 transition-transform shadow-xl font-bold text-xs">
                          <SafeIcon icon={FiFolder} className="w-4 h-4" />
                          <span>Library</span>
                        </button>
                      )}
                      <button onClick={() => setStyles({ ...styles, backgroundImage: '' })} className="flex items-center space-x-1.5 px-3 py-2 bg-red-500 text-white rounded-xl hover:scale-105 transition-transform shadow-xl font-bold text-xs">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className={`flex flex-col items-center justify-center aspect-video border-4 border-dashed rounded-3xl cursor-pointer transition-all ${isUploading ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200 hover:border-teal-400 hover:bg-teal-50/50'}`}>
                      <div className="flex flex-col items-center space-y-4 px-6 text-center w-full">
                        <div className={`p-5 rounded-2xl ${isUploading ? 'bg-teal-500 text-white shadow-lg shadow-teal-200/50' : 'bg-white text-gray-300 shadow-sm'}`}>
                          <SafeIcon icon={isUploading ? FiUploadCloud : FiImage} className={`w-8 h-8 ${isUploading ? 'animate-bounce' : ''}`} />
                        </div>
                        <div>
                          <span className="text-sm font-black text-gray-900 block">{isUploading ? 'Uploading Your Image...' : 'Drop your background here'}</span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1 block">PNG, JPG or WebP up to 10MB</span>
                        </div>
                        {isUploading && (
                          <div className="w-full max-w-[200px] bg-teal-100 h-1.5 rounded-full mt-4 overflow-hidden">
                            <motion.div initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} className="bg-teal-500 h-full" />
                          </div>
                        )}
                      </div>
                      <input type="file" className="hidden" accept="image/png,image/jpeg,image/webp" onChange={handleFileUpload} disabled={isUploading} />
                    </label>

                    {onOpenLibrary && (
                      <button onClick={() => onOpenLibrary((image) => { setStyles(prev => ({ ...prev, backgroundImage: image.url, backgroundType: 'image' })); })} className="w-full py-4 border-2 border-dashed border-blue-200 bg-blue-50/50 rounded-2xl flex items-center justify-center space-x-3 hover:border-blue-400 hover:bg-blue-50 transition-all">
                        <SafeIcon icon={FiFolder} className="w-5 h-5 text-blue-500" />
                        <span className="font-bold text-blue-600">Choose from Library</span>
                      </button>
                    )}

                    <div className="border-2 border-dashed border-[#c8f542]/40 bg-[#c8f542]/5 rounded-2xl p-5 space-y-4">
                      <div className="flex items-center space-x-2 mb-1">
                        <div className="w-6 h-6 bg-[#c8f542] rounded-lg flex items-center justify-center">
                          <SafeIcon icon={FiZap} className="w-3.5 h-3.5 text-black" />
                        </div>
                        <span className="text-[10px] font-black text-gray-700 uppercase tracking-widest">Generate Image with AI</span>
                      </div>
                      <p className="text-[10px] text-gray-500 leading-relaxed">Describe the background image you want and our AI will create it for you.</p>

                      <textarea
                        value={aiPrompt}
                        onChange={(e) => setAiPrompt(e.target.value)}
                        placeholder="e.g. A vibrant South African market scene at golden hour with colourful fabrics and warm sunlight..."
                        rows={3}
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-3 text-sm font-medium text-gray-800 placeholder-gray-400 outline-none focus:ring-2 focus:ring-[#c8f542]/40 focus:border-[#c8f542]/60 transition-all resize-none"
                      />

                      {aiError && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start space-x-2 text-red-600 text-xs font-medium">
                          <SafeIcon icon={FiAlertCircle} className="w-4 h-4 shrink-0 mt-0.5" />
                          <span>{aiError}</span>
                        </div>
                      )}

                      {aiGeneratedUrl && (
                        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                          <div className="relative rounded-xl overflow-hidden aspect-video border-2 border-[#c8f542]/40 shadow-lg">
                            <img src={aiGeneratedUrl} alt="AI Generated" className="w-full h-full object-cover" />
                            <div className="absolute top-2 left-2 bg-[#c8f542] text-black text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full">
                              ✓ Saved to Library
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button onClick={handleUseAIImage} className="flex-1 py-2.5 bg-[#c8f542] hover:bg-[#d4ff50] text-black font-black text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center space-x-1.5">
                              <SafeIcon icon={FiImage} className="w-3.5 h-3.5" />
                              <span>Use This Image</span>
                            </button>
                            <button onClick={() => { setAiGeneratedUrl(''); setAiError(''); }} className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold text-xs rounded-xl transition-all">
                              Discard
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {!aiGeneratedUrl && (
                        <button
                          onClick={handleGenerateAIImage}
                          disabled={aiLoading || !aiPrompt.trim()}
                          className="w-full py-3 bg-gray-900 hover:bg-black text-white font-black text-xs uppercase tracking-widest rounded-xl transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {aiLoading ? (
                            <>
                              <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
                              <span>Generating & Saving...</span>
                            </>
                          ) : (
                            <>
                              <SafeIcon icon={FiZap} className="w-4 h-4" />
                              <span>Generate Image</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {styles.backgroundImage && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="bg-gray-900 rounded-2xl p-5 space-y-4">
                    
                    {/* Parallax Toggle */}
                    <div className="flex items-center justify-between pb-4 border-b border-gray-800">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-[#c8f542]/20 rounded-lg"><SafeIcon icon={FiLayers} className="w-4 h-4 text-[#c8f542]"/></div>
                        <div>
                          <p className="text-sm font-bold text-white">Parallax Effect</p>
                          <p className="text-[10px] text-gray-400 uppercase tracking-widest mt-0.5">Image moves while scrolling</p>
                        </div>
                      </div>
                      <button onClick={() => setStyles({...styles, parallax: !styles.parallax})} className={`relative w-11 h-6 rounded-full transition-colors ${styles.parallax ? 'bg-[#c8f542]' : 'bg-gray-700'}`}>
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-black rounded-full transition-transform ${styles.parallax ? 'translate-x-5' : 'translate-x-0'}`} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {styles.parallax && (
                        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="space-y-3 pb-4 border-b border-gray-800 overflow-hidden">
                          <div className="flex justify-between items-center">
                            <label className="text-[10px] font-black text-[#c8f542] uppercase tracking-widest">Effect Speed</label>
                            <span className="text-xs font-black text-gray-300">{Math.round(styles.parallaxSpeed * 100)}%</span>
                          </div>
                          <input
                            type="range"
                            min="0.1"
                            max="0.8"
                            step="0.1"
                            value={styles.parallaxSpeed}
                            onChange={(e) => setStyles({ ...styles, parallaxSpeed: parseFloat(e.target.value) })}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer border border-gray-700"
                            style={{ 
                              background: `linear-gradient(to right, #c8f542 ${((styles.parallaxSpeed - 0.1) / 0.7) * 100}%, #374151 ${((styles.parallaxSpeed - 0.1) / 0.7) * 100}%)`,
                            }}
                          />
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="mb-4">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest block mb-2">Image Fit</label>
                      <div className="flex p-1 bg-gray-800 rounded-xl">
                        {['cover', 'contain', '100% 100%'].map(size => (
                          <button
                            key={size}
                            onClick={() => setStyles({...styles, backgroundSize: size})}
                            className={`flex-1 py-1.5 rounded-lg text-xs font-bold capitalize transition-all ${styles.backgroundSize === size ? 'bg-teal-500 text-white shadow-md' : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'}`}
                          >
                            {size === 'cover' ? 'Fill' : size === 'contain' ? 'Fit' : 'Stretch'}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-gray-700 rounded-lg">
                          <SafeIcon icon={FiMoon} className="w-4 h-4 text-gray-300" />
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-white uppercase tracking-widest">Dark Overlay</p>
                          <p className="text-[9px] text-gray-400 font-medium">Improve text readability</p>
                        </div>
                      </div>
                      <span className={`text-sm font-black px-3 py-1 rounded-full ${overlayPercent > 0 ? 'bg-teal-500 text-white' : 'bg-gray-700 text-gray-400'}`}>
                        {overlayPercent}%
                      </span>
                    </div>

                    <input
                      type="range"
                      min="0"
                      max="0.9"
                      step="0.05"
                      value={styles.overlayOpacity}
                      onChange={(e) => setStyles({ ...styles, overlayOpacity: parseFloat(e.target.value) })}
                      className="w-full h-2 rounded-lg appearance-none cursor-pointer accent-teal-400 border border-gray-700"
                      style={{ background: `linear-gradient(to right, #2dd4bf ${overlayPercent}%, #374151 ${overlayPercent}%)` }}
                    />

                    <div className="flex justify-between text-[9px] font-black text-gray-500 uppercase px-0.5">
                      <span>None</span>
                      <span>Full Dark</span>
                    </div>

                    <div className="flex items-center space-x-3 pt-1">
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest flex-1">Overlay Color</p>
                      <div className="flex items-center space-x-2">
                        {['#000000', '#1e1b4b', '#0f172a', '#4a044e', '#1a2e05'].map(c => (
                          <button
                            key={c}
                            onClick={() => setStyles({ ...styles, overlayColor: c })}
                            className={`w-7 h-7 rounded-full border-2 transition-all hover:scale-110 ${styles.overlayColor === c ? 'border-teal-400 ring-2 ring-teal-400/30' : 'border-gray-600'}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                        <div className="relative w-7 h-7 rounded-full overflow-hidden border-2 border-gray-600">
                          <input type="color" value={styles.overlayColor} onChange={(e) => setStyles({ ...styles, overlayColor: e.target.value })} className="absolute -top-1 -left-1 w-12 h-12 cursor-pointer opacity-0" />
                          <div className="w-full h-full rounded-full" style={{ backgroundColor: styles.overlayColor }} />
                        </div>
                      </div>
                    </div>

                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="bg-gray-50 p-6 rounded-[2rem] space-y-4 border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-teal-100 rounded-lg"><SafeIcon icon={FiMaximize} className="w-4 h-4 text-teal-600"/></div>
                <div>
                  <p className="text-sm font-bold text-gray-800">Full Screen Height</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-widest mt-0.5">Stretch section to fill the screen</p>
                </div>
              </div>
              <button onClick={() => setStyles({...styles, fullScreen: !styles.fullScreen})} className={`relative w-11 h-6 rounded-full transition-colors ${styles.fullScreen ? 'bg-teal-500' : 'bg-gray-300'}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${styles.fullScreen ? 'translate-x-5' : 'translate-x-0'}`} />
              </button>
            </div>

            {!styles.fullScreen && (
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center">
                    Section Padding
                  </label>
                  <span className="bg-white px-3 py-1 rounded-full text-xs font-black text-teal-600 border border-teal-100 shadow-sm">
                    {styles.paddingTop}px
                  </span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="240"
                  value={styles.paddingTop}
                  onChange={(e) => setStyles({ ...styles, paddingTop: parseInt(e.target.value), paddingBottom: parseInt(e.target.value) })}
                  className="w-full h-2 bg-white rounded-lg appearance-none cursor-pointer accent-teal-500 border border-gray-100"
                />
                <div className="flex justify-between text-[8px] font-black text-gray-300 uppercase px-1">
                  <span>Compact</span>
                  <span>Spacious</span>
                </div>
              </div>
            )}
          </div>

          <div className="bg-gray-50 p-6 rounded-[2rem] space-y-4 border border-gray-100">
            <div className="flex justify-between items-center mb-1">
              <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest flex items-center">
                <SafeIcon icon={FiLink} className="mr-2 text-teal-500" />
                Anchor Point ID
              </label>
            </div>
            <input 
              type="text" 
              value={styles.anchorId}
              onChange={(e) => setStyles({...styles, anchorId: e.target.value.replace(/[^a-zA-Z0-9-_]/g, '')})}
              className="w-full p-3 bg-white border border-gray-200 rounded-xl outline-none focus:ring-2 focus:ring-teal-400 transition-all text-sm font-medium text-gray-700"
              placeholder="e.g. pricing-section"
            />
            <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
              Used to link directly to this section from the menu. Alphanumeric and hyphens only.
            </p>
          </div>

        </div>

        <div className="p-8 bg-gray-50 flex space-x-4 border-t border-gray-100">
          <button onClick={onClose} className="flex-1 py-4 font-black text-gray-400 hover:text-gray-600 uppercase tracking-widest text-xs transition-colors">
            Discard
          </button>
          <button onClick={() => { onUpdate(styles); onClose(); }} className="flex-[2] py-4 bg-gray-900 text-white font-black uppercase tracking-widest text-xs rounded-2xl shadow-xl shadow-gray-200 hover:bg-teal-600 hover:shadow-teal-100 transition-all transform active:scale-95">
            Apply Settings
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ContainerPopup;